import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, HeartHandshake, MoreVertical, ArrowRightCircle, XCircle,
  Eye, Save, CheckCircle2, Calendar,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const PATHWAYS = [
  { value: 'self',                  label: 'Self' },
  { value: 'family_representative', label: 'Family / Representative' },
  { value: 'professional',          label: 'Professional Referral' },
  { value: 'provider',              label: 'Provider' },
];

const SOURCES = [
  { value: 'website_contact', label: 'Website Contact' },
  { value: 'phone',           label: 'Phone Call' },
  { value: 'referral',        label: 'Referral' },
  { value: 'nhs',             label: 'NHS' },
  { value: 'local_authority', label: 'Local Authority' },
  { value: 'other',           label: 'Other' },
];

const URGENCY_OPTIONS = [
  { value: 'routine',   label: 'Routine' },
  { value: 'urgent',    label: 'Urgent' },
  { value: 'emergency', label: 'Emergency' },
];

const PATHWAY_LABELS  = Object.fromEntries(PATHWAYS.map(({ value, label }) => [value, label]));
const SOURCE_LABELS   = Object.fromEntries(SOURCES.map(({ value, label }) => [value, label]));
const URGENCY_LABELS  = Object.fromEntries(URGENCY_OPTIONS.map(({ value, label }) => [value, label]));

const STATUS_COLORS = {
  new:                'bg-blue-100 text-blue-700',
  in_review:          'bg-amber-100 text-amber-700',
  assessment_booked:  'bg-purple-100 text-purple-700',
  converted:          'bg-green-100 text-green-700',
  not_suitable:       'bg-orange-100 text-orange-700',
  closed:             'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  new:                'New',
  in_review:          'In Review',
  assessment_booked:  'Assessment Booked',
  converted:          'Converted',
  not_suitable:       'Not Suitable',
  closed:             'Closed',
};

const ALL_STATUSES = ['new', 'in_review', 'assessment_booked', 'converted', 'not_suitable', 'closed'];

const URGENCY_COLORS = {
  routine:   'bg-gray-100 text-gray-600',
  urgent:    'bg-amber-100 text-amber-700',
  emergency: 'bg-red-100 text-red-700',
};

const EMPTY_CREATE_FORM = {
  pathway: '',
  source: '',
  enquirer_name: '',
  enquirer_relationship: '',
  enquirer_phone: '',
  enquirer_email: '',
  service_user_name: '',
  service_user_postcode: '',
  care_needs_summary: '',
  urgency: 'routine',
  notes: '',
};

const EMPTY_CONVERT_FORM = {
  full_name: '',
  phone: '',
  email: '',
  address_line_1: '',
  postcode: '',
  notes: '',
};

const EMPTY_CLOSE_FORM = {
  outcome: 'closed',
  close_reason: '',
};

export default function CareEnquiriesPage() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);

  // Convert-to-service-user dialog
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertForm, setConvertForm] = useState(EMPTY_CONVERT_FORM);
  const [converting, setConverting] = useState(false);

  // Close dialog
  const [closeTarget, setCloseTarget] = useState(null);
  const [closeForm, setCloseForm] = useState(EMPTY_CLOSE_FORM);
  const [closing, setClosing] = useState(false);

  // Detail drawer
  const [drawerEnquiry, setDrawerEnquiry] = useState(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const fetchEnquiries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/care-enquiries`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEnquiries(data.care_enquiries || []);
      } else {
        toast.error('Failed to load care enquiries');
      }
    } catch {
      toast.error('Failed to load care enquiries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    setLoading(true);
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.enquirer_name.trim()) {
      toast.error('Enquirer name is required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/care-enquiries`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Enquiry created: ${data.care_enquiry.enquiry_code}`);
        setShowCreateDialog(false);
        setCreateForm(EMPTY_CREATE_FORM);
        fetchEnquiries();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create care enquiry');
      }
    } catch {
      toast.error('Failed to create care enquiry');
    } finally {
      setSaving(false);
    }
  };

  const openConvertDialog = (enq) => {
    setConvertTarget(enq);
    setConvertForm({
      full_name: enq.service_user_name || '',
      phone: enq.enquirer_phone || '',
      email: enq.enquirer_email || '',
      address_line_1: '',
      postcode: enq.service_user_postcode || '',
      notes: '',
    });
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    if (!convertForm.full_name.trim()) {
      toast.error('Service user name is required');
      return;
    }
    setConverting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/care-enquiries/${convertTarget.id}/convert-to-service-user`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(convertForm),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Service user created: ${data.service_user?.full_name || convertForm.full_name}`);
        setConvertTarget(null);
        setConvertForm(EMPTY_CONVERT_FORM);
        setDrawerEnquiry(null);
        fetchEnquiries();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to convert enquiry');
      }
    } catch {
      toast.error('Failed to convert enquiry');
    } finally {
      setConverting(false);
    }
  };

  const openCloseDialog = (enq) => {
    setCloseTarget(enq);
    setCloseForm(EMPTY_CLOSE_FORM);
  };

  const handleClose = async (e) => {
    e.preventDefault();
    setClosing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/care-enquiries/${closeTarget.id}/close`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(closeForm),
      });
      if (res.ok) {
        toast.success(`${closeTarget.enquiry_code} closed`);
        setCloseTarget(null);
        setCloseForm(EMPTY_CLOSE_FORM);
        setDrawerEnquiry(null);
        fetchEnquiries();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to close enquiry');
      }
    } catch {
      toast.error('Failed to close enquiry');
    } finally {
      setClosing(false);
    }
  };

  // ── Drawer helpers ────────────────────────────────────────────────────────
  const openDrawer = (enq) => {
    setDrawerEnquiry(enq);
    setEditingNotes(enq.notes || '');
  };

  const handleSaveNotes = async () => {
    if (!drawerEnquiry) return;
    setSavingNotes(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/care-enquiries/${drawerEnquiry.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: editingNotes }),
      });
      if (res.ok) {
        const data = await res.json();
        setDrawerEnquiry(data.care_enquiry);
        toast.success('Notes saved');
        fetchEnquiries();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save notes');
      }
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!drawerEnquiry) return;
    setChangingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/care-enquiries/${drawerEnquiry.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const data = await res.json();
        setDrawerEnquiry(data.care_enquiry);
        toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
        fetchEnquiries();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update status');
      }
    } catch {
      toast.error('Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const countByStatus = ALL_STATUSES.reduce((acc, s) => {
    acc[s] = enquiries.filter((e) => e.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="care-enquiries-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Care Enquiries</h1>
          <p className="text-sm text-text-muted mt-1">
            Track incoming enquiries and convert them to service users
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-enquiry-btn">
              <Plus className="h-4 w-4 mr-2" />
              Log Enquiry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Log Care Enquiry</DialogTitle>
              <DialogDescription>
                Record a new care enquiry. Enquirer name is required.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {/* Pathway & Source */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ce-pathway">Pathway</Label>
                  <select
                    id="ce-pathway"
                    value={createForm.pathway}
                    onChange={(e) => setCreateForm({ ...createForm, pathway: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="">Select…</option>
                    {PATHWAYS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ce-source">Source</Label>
                  <select
                    id="ce-source"
                    value={createForm.source}
                    onChange={(e) => setCreateForm({ ...createForm, source: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="">Select…</option>
                    {SOURCES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Enquirer details */}
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-text-primary mb-3">Enquirer Details</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ce-eq-name">Name *</Label>
                      <Input
                        id="ce-eq-name"
                        value={createForm.enquirer_name}
                        onChange={(e) => setCreateForm({ ...createForm, enquirer_name: e.target.value })}
                        placeholder="Enquirer's full name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ce-eq-rel">Relationship</Label>
                      <Input
                        id="ce-eq-rel"
                        value={createForm.enquirer_relationship}
                        onChange={(e) => setCreateForm({ ...createForm, enquirer_relationship: e.target.value })}
                        placeholder="e.g. Daughter"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ce-eq-phone">Phone</Label>
                      <Input
                        id="ce-eq-phone"
                        value={createForm.enquirer_phone}
                        onChange={(e) => setCreateForm({ ...createForm, enquirer_phone: e.target.value })}
                        placeholder="07xxx xxxxxx"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ce-eq-email">Email</Label>
                      <Input
                        id="ce-eq-email"
                        type="email"
                        value={createForm.enquirer_email}
                        onChange={(e) => setCreateForm({ ...createForm, enquirer_email: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Service user details */}
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-text-primary mb-3">Person Needing Care</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ce-su-name">Full Name</Label>
                      <Input
                        id="ce-su-name"
                        value={createForm.service_user_name}
                        onChange={(e) => setCreateForm({ ...createForm, service_user_name: e.target.value })}
                        placeholder="Person's name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ce-su-postcode">Postcode</Label>
                      <Input
                        id="ce-su-postcode"
                        value={createForm.service_user_postcode}
                        onChange={(e) => setCreateForm({ ...createForm, service_user_postcode: e.target.value })}
                        placeholder="SW1A 1AA"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ce-care-needs">Care Needs Summary</Label>
                    <Textarea
                      id="ce-care-needs"
                      value={createForm.care_needs_summary}
                      onChange={(e) => setCreateForm({ ...createForm, care_needs_summary: e.target.value })}
                      rows={2}
                      placeholder="Brief description of care needs"
                    />
                  </div>
                </div>
              </div>

              {/* Urgency & notes */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ce-urgency">Urgency</Label>
                  <select
                    id="ce-urgency"
                    value={createForm.urgency}
                    onChange={(e) => setCreateForm({ ...createForm, urgency: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {URGENCY_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ce-notes">Notes</Label>
                <Textarea
                  id="ce-notes"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Optional additional notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowCreateDialog(false); setCreateForm(EMPTY_CREATE_FORM); }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Log Enquiry'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Convert-to-service-user dialog */}
      <Dialog
        open={!!convertTarget}
        onOpenChange={(open) => { if (!open) { setConvertTarget(null); setConvertForm(EMPTY_CONVERT_FORM); } }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Convert to Service User</DialogTitle>
            <DialogDescription>
              {convertTarget?.enquiry_code} — {convertTarget?.enquirer_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConvert} className="space-y-4">
            {/* Care needs summary — read-only reference */}
            {convertTarget?.care_needs_summary && (
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                <p className="text-xs font-semibold text-blue-700 mb-1">Care Needs (from enquiry)</p>
                <p className="text-sm text-blue-800 whitespace-pre-wrap">{convertTarget.care_needs_summary}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="conv-name">Full Name *</Label>
              <Input
                id="conv-name"
                value={convertForm.full_name}
                onChange={(e) => setConvertForm({ ...convertForm, full_name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="conv-phone">Phone</Label>
                <Input
                  id="conv-phone"
                  value={convertForm.phone}
                  onChange={(e) => setConvertForm({ ...convertForm, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conv-email">Email</Label>
                <Input
                  id="conv-email"
                  type="email"
                  value={convertForm.email}
                  onChange={(e) => setConvertForm({ ...convertForm, email: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="conv-addr">Address Line 1</Label>
                <Input
                  id="conv-addr"
                  value={convertForm.address_line_1}
                  onChange={(e) => setConvertForm({ ...convertForm, address_line_1: e.target.value })}
                  placeholder="Street address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conv-postcode">Postcode</Label>
                <Input
                  id="conv-postcode"
                  value={convertForm.postcode}
                  onChange={(e) => setConvertForm({ ...convertForm, postcode: e.target.value })}
                  placeholder="e.g. ME14 1LH"
                  data-testid="conv-postcode"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="conv-notes">Notes</Label>
              <Textarea
                id="conv-notes"
                value={convertForm.notes}
                onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setConvertTarget(null); setConvertForm(EMPTY_CONVERT_FORM); }}
                disabled={converting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={converting}>
                {converting ? 'Creating…' : 'Create Service User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close enquiry dialog */}
      <Dialog
        open={!!closeTarget}
        onOpenChange={(open) => { if (!open) { setCloseTarget(null); setCloseForm(EMPTY_CLOSE_FORM); } }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Close Enquiry</DialogTitle>
            <DialogDescription>
              {closeTarget?.enquiry_code} — {closeTarget?.enquirer_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClose} className="space-y-4">
            <div className="space-y-2">
              <Label>Outcome</Label>
              <div className="flex gap-4">
                {[
                  { value: 'closed',       label: 'Closed' },
                  { value: 'not_suitable', label: 'Not Suitable' },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="close-outcome"
                      value={value}
                      checked={closeForm.outcome === value}
                      onChange={() => setCloseForm({ ...closeForm, outcome: value })}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="close-reason">Reason (optional)</Label>
              <Textarea
                id="close-reason"
                value={closeForm.close_reason}
                onChange={(e) => setCloseForm({ ...closeForm, close_reason: e.target.value })}
                rows={2}
                placeholder="Brief reason for closing"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setCloseTarget(null); setCloseForm(EMPTY_CLOSE_FORM); }}
                disabled={closing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={closing} variant="destructive">
                {closing ? 'Closing…' : 'Close Enquiry'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Detail drawer ──────────────────────────────────────────── */}
      <Dialog
        open={!!drawerEnquiry}
        onOpenChange={(open) => { if (!open) setDrawerEnquiry(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="enquiry-drawer">
          {drawerEnquiry && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <DialogTitle className="font-mono text-primary">
                    {drawerEnquiry.enquiry_code}
                  </DialogTitle>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[drawerEnquiry.status] || 'bg-gray-100 text-gray-500'}`}>
                    {STATUS_LABELS[drawerEnquiry.status] || drawerEnquiry.status}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${URGENCY_COLORS[drawerEnquiry.urgency] || 'bg-gray-100 text-gray-500'}`}>
                    {URGENCY_LABELS[drawerEnquiry.urgency] || drawerEnquiry.urgency}
                  </span>
                </div>
              </DialogHeader>

              {/* Meta row */}
              <div className="grid grid-cols-3 gap-3 text-sm py-2 border-b border-gray-100">
                <div>
                  <p className="text-xs text-text-muted">Pathway</p>
                  <p className="font-medium text-text-primary">{PATHWAY_LABELS[drawerEnquiry.pathway] || drawerEnquiry.pathway || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Source</p>
                  <p className="font-medium text-text-primary">{SOURCE_LABELS[drawerEnquiry.source] || drawerEnquiry.source || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Received</p>
                  <p className="font-medium text-text-primary">{fmtDate(drawerEnquiry.created_at)}</p>
                </div>
              </div>

              {/* Enquirer */}
              <div className="pt-1">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Enquirer</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <p className="text-xs text-text-muted">Name</p>
                    <p className="text-text-primary font-medium">{drawerEnquiry.enquirer_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Relationship</p>
                    <p className="text-text-primary">{drawerEnquiry.enquirer_relationship || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Phone</p>
                    {drawerEnquiry.enquirer_phone
                      ? <a href={`tel:${drawerEnquiry.enquirer_phone}`} className="text-primary hover:underline">{drawerEnquiry.enquirer_phone}</a>
                      : <p className="text-text-muted">—</p>}
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Email</p>
                    {drawerEnquiry.enquirer_email
                      ? <a href={`mailto:${drawerEnquiry.enquirer_email}`} className="text-primary hover:underline truncate block">{drawerEnquiry.enquirer_email}</a>
                      : <p className="text-text-muted">—</p>}
                  </div>
                </div>
              </div>

              {/* Person needing care */}
              <div className="pt-1 border-t border-gray-100">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Person Needing Care</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <p className="text-xs text-text-muted">Name</p>
                    <p className="text-text-primary">{drawerEnquiry.service_user_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Postcode</p>
                    <p className="text-text-primary">{drawerEnquiry.service_user_postcode || '—'}</p>
                  </div>
                </div>
              </div>

              {/* Care needs */}
              <div className="pt-1 border-t border-gray-100">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Care Needs Summary</p>
                {drawerEnquiry.care_needs_summary
                  ? (
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-text-primary whitespace-pre-wrap" data-testid="drawer-care-summary">
                      {drawerEnquiry.care_needs_summary}
                    </div>
                  )
                  : <p className="text-sm text-text-muted italic">No care needs summary recorded.</p>
                }
              </div>

              {/* Close reason if present */}
              {drawerEnquiry.close_reason && (
                <div className="pt-1 border-t border-gray-100">
                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Close Reason</p>
                  <p className="text-sm text-text-primary">{drawerEnquiry.close_reason}</p>
                </div>
              )}

              {/* Notes */}
              <div className="pt-1 border-t border-gray-100">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Internal Notes</p>
                <Textarea
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  rows={3}
                  placeholder="Add internal notes (only visible to admin/manager)…"
                  className="text-sm"
                  data-testid="drawer-notes"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveNotes}
                    disabled={savingNotes || editingNotes === (drawerEnquiry.notes || '')}
                    data-testid="drawer-save-notes"
                  >
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {savingNotes ? 'Saving…' : 'Save Notes'}
                  </Button>
                </div>
              </div>

              {/* Status actions */}
              {!['converted', 'closed', 'not_suitable'].includes(drawerEnquiry.status) && (
                <div className="pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
                  {drawerEnquiry.status === 'new' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange('in_review')}
                      disabled={changingStatus}
                      data-testid="drawer-btn-in-review"
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                      Mark In Review
                    </Button>
                  )}
                  {(drawerEnquiry.status === 'new' || drawerEnquiry.status === 'in_review') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange('assessment_booked')}
                      disabled={changingStatus}
                      data-testid="drawer-btn-assessment"
                      className="text-purple-700 border-purple-300 hover:bg-purple-50"
                    >
                      <Calendar className="h-3.5 w-3.5 mr-1.5" />
                      Assessment Booked
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    onClick={() => { openConvertDialog(drawerEnquiry); }}
                    disabled={changingStatus}
                    data-testid="drawer-btn-convert"
                  >
                    <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />
                    Convert to Service User
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { openCloseDialog(drawerEnquiry); }}
                    disabled={changingStatus}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="drawer-btn-close"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1.5" />
                    Close
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['new', 'in_review', 'assessment_booked', 'converted'].map((s) => (
          <div key={s} className="p-4 rounded-xl bg-white border border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${STATUS_COLORS[s]?.split(' ')[0] || 'bg-gray-100'}`}>
                <HeartHandshake className={`h-5 w-5 ${STATUS_COLORS[s]?.split(' ')[1] || 'text-gray-500'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{countByStatus[s] || 0}</p>
                <p className="text-xs text-text-muted">{STATUS_LABELS[s]}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === 'all' ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-text-muted hover:bg-gray-50'}`}
        >
          All
        </button>
        {ALL_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === s ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-text-muted hover:bg-gray-50'}`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {/* List */}
      {enquiries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <HeartHandshake className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No Care Enquiries</h3>
          <p className="text-sm text-text-muted mb-4">
            {statusFilter !== 'all'
              ? `No ${STATUS_LABELS[statusFilter]?.toLowerCase()} enquiries`
              : 'Log your first care enquiry'}
          </p>
          {statusFilter === 'all' && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Log Enquiry
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Enquirer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Person Needing Care</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Pathway</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Urgency</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Created</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {enquiries.map((enq) => (
                  <tr
                    key={enq.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => openDrawer(enq)}
                    data-testid={`enquiry-row-${enq.id}`}
                  >
                    <td className="px-4 py-4">
                      <span className="font-mono text-sm font-semibold text-primary">
                        {enq.enquiry_code}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm font-medium text-text-primary">{enq.enquirer_name}</p>
                      <p className="text-xs text-text-muted">
                        {enq.enquirer_relationship
                          ? `${enq.enquirer_relationship} · `
                          : ''}
                        {[enq.enquirer_phone, enq.enquirer_email].filter(Boolean).join(' · ') || ''}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-text-primary">{enq.service_user_name || '—'}</p>
                      {enq.service_user_postcode && (
                        <p className="text-xs text-text-muted">{enq.service_user_postcode}</p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        {PATHWAY_LABELS[enq.pathway] || enq.pathway || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${URGENCY_COLORS[enq.urgency] || 'bg-gray-100 text-gray-500'}`}>
                        {URGENCY_LABELS[enq.urgency] || enq.urgency || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-xs text-text-muted">
                        {SOURCE_LABELS[enq.source] || enq.source || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-xs text-text-muted whitespace-nowrap">
                        {fmtDate(enq.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[enq.status] || 'bg-gray-100 text-gray-500'}`}>
                        {STATUS_LABELS[enq.status] || enq.status}
                      </span>
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => openDrawer(enq)}
                          >
                            <Eye className="h-4 w-4 mr-2 text-text-muted" />
                            View Details
                          </DropdownMenuItem>
                          {enq.status !== 'converted' &&
                            enq.status !== 'closed' &&
                            enq.status !== 'not_suitable' && (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => openConvertDialog(enq)}
                            >
                              <ArrowRightCircle className="h-4 w-4 mr-2 text-primary" />
                              Convert to Service User
                            </DropdownMenuItem>
                          )}
                          {enq.status !== 'closed' &&
                            enq.status !== 'not_suitable' && (
                            <DropdownMenuItem
                              className="text-red-600 cursor-pointer"
                              onClick={() => openCloseDialog(enq)}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Close Enquiry
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-50 bg-gray-50">
            <p className="text-xs text-text-muted">
              {enquiries.length} enqu{enquiries.length !== 1 ? 'iries' : 'iry'}
              {statusFilter !== 'all' ? ` — ${STATUS_LABELS[statusFilter]}` : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
